import { mat4, quat, ReadonlyMat4, vec2, vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import {
    DicomContour,
    ContourGeometricType,
} from '~/domain/dicom/dicomstructureset';
import { marchingSquares, simplify } from '~/engine/core/utils/marchingSquares';
import { Image3D } from '~/domain/patient/image3d';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { resampleUint8Grid } from '~/domain/services/resampleGrid';
import {
    DeformableTransform,
    mapPointThroughDeformation,
} from '~/domain/dvf/deformableTransform';
import { dvfMaxMagnitude } from '~/domain/dvf/dvfField';

/**
 * Resamples SDF volumes between coordinate systems via registration
 * and extracts DICOM contours from the result.
 *
 * Uses the existing resampleUint8Grid utility for the heavy lifting.
 */
export class SdfResamplingService {
    /**
     * Resamples a source SDF through a registration transform onto a
     * target image grid, then extracts per-slice contours suitable for
     * DICOM RTSTRUCT storage.
     *
     * @param sourceTexture  Source ROI's ContourTexture (has SDF pixels + grid)
     * @param targetImage    Target image to generate contours for
     * @param sourceToTarget mat4 that maps source-FOR coords → target-FOR coords
     * @returns DicomContour[] ready to assign to the new ROI
     */
    static async resampleAndExtractContours(
        sourceTexture: ContourTexture,
        targetImage: Image3D,
        sourceToTarget: ReadonlyMat4
    ): Promise<DicomContour[]> {
        const sourcePixels = sourceTexture.pixels;
        const sourceGrid = sourceTexture.textureGrid;

        if (
            !sourcePixels ||
            sourcePixels.length === 0 ||
            sourceTexture.isEmpty
        ) {
            return [];
        }

        // 1. Build target SDF grid
        const targetSdfGrid = this.buildTargetSdfGrid(
            sourceGrid,
            targetImage,
            sourceToTarget
        );
        if (!targetSdfGrid) {
            return [];
        }

        // 2. Flatten source pixels into a single Uint8Array
        const srcNx = sourceGrid.sizeX;
        const srcNy = sourceGrid.sizeY;
        const srcNz = sourceGrid.sizeZ;
        const flatSrc = new Uint8Array(srcNx * srcNy * srcNz);
        for (let z = 0; z < srcNz; z++) {
            flatSrc.set(sourcePixels[z], z * srcNx * srcNy);
        }

        // 3. Resample using existing utility
        // patientSrcFromDst maps target world → source world = inverse of sourceToTarget
        const targetToSource = mat4.invert(mat4.create(), sourceToTarget);

        const { pixels: flatResult } = resampleUint8Grid(
            flatSrc,
            sourceGrid,
            targetSdfGrid,
            {
                patientSrcFromDst: targetToSource,
                backgroundValue: 0,
                clampToEdge: false,
            }
        );

        // 4. Split back into per-slice arrays
        const tgtNx = targetSdfGrid.sizeX;
        const tgtNy = targetSdfGrid.sizeY;
        const tgtNz = targetSdfGrid.sizeZ;
        const planeSize = tgtNx * tgtNy;
        const resampledPixels: Uint8Array[] = [];
        for (let z = 0; z < tgtNz; z++) {
            resampledPixels.push(
                flatResult.subarray(z * planeSize, (z + 1) * planeSize)
            );
        }

        // 5. Clean up interpolation artifacts
        const threshold = ContourTexture.SDF_THRESHOLD;
        const cleanedPixels = this.cleanupResampledSdf(
            resampledPixels,
            tgtNx,
            tgtNy,
            tgtNz,
            threshold,
            2
        );

        // 6. Extract contours
        return this.extractContours(cleanedPixels, targetSdfGrid, targetImage);
    }

    /**
     * Builds a Grid for the target SDF that covers the region where
     * the source structure lands after transformation. Uses the target
     * image's resolution and orientation. Clamped to image bounds.
     */
    private static buildTargetSdfGrid(
        sourceGrid: Grid,
        targetImage: Image3D,
        sourceToTarget: ReadonlyMat4,
        extraMarginMm = 0
    ): Grid | null {
        const tgtImageGrid = targetImage.grid;
        const tgtRes = tgtImageGrid.getRes();
        const marginMm = ContourTexture.DEFAULT_MARGIN + extraMarginMm;

        const srcSize = sourceGrid.getSize();
        const minImg = vec3.fromValues(Infinity, Infinity, Infinity);
        const maxImg = vec3.fromValues(-Infinity, -Infinity, -Infinity);

        for (const dx of [0, srcSize[0] as number]) {
            for (const dy of [0, srcSize[1] as number]) {
                for (const dz of [0, srcSize[2] as number]) {
                    const srcWorld = sourceGrid.toWorldSpace(
                        vec3.fromValues(dx, dy, dz)
                    );
                    const tgtWorld = vec3.transformMat4(
                        vec3.create(),
                        srcWorld,
                        sourceToTarget
                    );
                    const imgCoord = tgtImageGrid.toImageMm(tgtWorld);
                    vec3.min(minImg, minImg, imgCoord);
                    vec3.max(maxImg, maxImg, imgCoord);
                }
            }
        }

        // Add margin
        vec3.sub(minImg, minImg, vec3.fromValues(marginMm, marginMm, marginMm));
        vec3.add(maxImg, maxImg, vec3.fromValues(marginMm, marginMm, marginMm));

        // Clamp to target image bounds
        const imageSizeMm = tgtImageGrid.getSizeMM();
        vec3.max(minImg, minImg, vec3.fromValues(0, 0, 0));
        vec3.min(
            maxImg,
            maxImg,
            vec3.fromValues(imageSizeMm[0], imageSizeMm[1], imageSizeMm[2])
        );

        // Snap to voxel boundaries
        const snapMin = tgtImageGrid.floorMmToVoxelBoundary(minImg);
        const snapMax = tgtImageGrid.ceilMmToVoxelBoundary(maxImg);

        const extent = vec3.sub(vec3.create(), snapMax, snapMin);
        const nx = Math.max(1, Math.ceil(extent[0] / tgtRes[0]));
        const ny = Math.max(1, Math.ceil(extent[1] / tgtRes[1]));
        const nz = Math.max(1, Math.ceil(extent[2] / tgtRes[2]));

        if (nx <= 0 || ny <= 0 || nz <= 0) {
            return null;
        }

        const minWorld = tgtImageGrid.fromImageMm(snapMin);

        const transform = mat4.fromRotationTranslation(
            mat4.create(),
            quat.fromMat3(quat.create(), tgtImageGrid.getOrientation()),
            minWorld
        );

        return new Grid(nx, ny, nz, tgtRes[0], tgtRes[1], tgtRes[2], transform);
    }

    static async resampleAndExtractContoursDeformable(
        sourceTexture: ContourTexture,
        targetImage: Image3D,
        transform: DeformableTransform
    ): Promise<{ contours: DicomContour[]; undefinedSamples: number }> {
        const sourcePixels = sourceTexture.pixels;
        const sourceGrid = sourceTexture.textureGrid;
        if (
            !sourcePixels ||
            sourcePixels.length === 0 ||
            sourceTexture.isEmpty
        ) {
            return { contours: [], undefinedSamples: 0 };
        }

        // Target box: bulk alignment inverted, padded by the field's largest displacement.
        const bulk = mat4.multiply(
            mat4.create(),
            transform.post,
            transform.pre
        );
        const sourceToTarget = mat4.create();
        if (!mat4.invert(sourceToTarget, bulk)) {
            return { contours: [], undefinedSamples: 0 };
        }
        const targetSdfGrid = this.buildTargetSdfGrid(
            sourceGrid,
            targetImage,
            sourceToTarget,
            dvfMaxMagnitude(transform.field)
        );
        if (!targetSdfGrid) {
            return { contours: [], undefinedSamples: 0 };
        }

        const srcNx = sourceGrid.sizeX;
        const srcNy = sourceGrid.sizeY;
        const srcNz = sourceGrid.sizeZ;
        const flatSrc = new Uint8Array(srcNx * srcNy * srcNz);
        for (let z = 0; z < srcNz; z++) {
            flatSrc.set(sourcePixels[z], z * srcNx * srcNy);
        }

        const { pixels: flatResult, undefinedSamples } = resampleUint8Grid(
            flatSrc,
            sourceGrid,
            targetSdfGrid,
            {
                patientSrcFromDstFn: (point) =>
                    mapPointThroughDeformation(transform, point),
                backgroundValue: 0,
                clampToEdge: false,
            }
        );

        const tgtNx = targetSdfGrid.sizeX;
        const tgtNy = targetSdfGrid.sizeY;
        const tgtNz = targetSdfGrid.sizeZ;
        const planeSize = tgtNx * tgtNy;
        const resampledPixels: Uint8Array[] = [];
        for (let z = 0; z < tgtNz; z++) {
            resampledPixels.push(
                flatResult.subarray(z * planeSize, (z + 1) * planeSize)
            );
        }

        const cleanedPixels = this.cleanupResampledSdf(
            resampledPixels,
            tgtNx,
            tgtNy,
            tgtNz,
            ContourTexture.SDF_THRESHOLD,
            2
        );

        return {
            contours: this.extractContours(
                cleanedPixels,
                targetSdfGrid,
                targetImage
            ),
            undefinedSamples,
        };
    }

    /**
     * Cleans up resampled SDF by removing isolated above-threshold voxels.
     */
    private static cleanupResampledSdf(
        pixels: Uint8Array[],
        nx: number,
        ny: number,
        nz: number,
        threshold: number,
        minNeighbors: number = 2
    ): Uint8Array[] {
        const result: Uint8Array[] = [];

        for (let z = 0; z < nz; z++) {
            const src = pixels[z];
            const dst = new Uint8Array(src.length);

            for (let y = 0; y < ny; y++) {
                for (let x = 0; x < nx; x++) {
                    const idx = y * nx + x;
                    if (src[idx] <= threshold) {
                        dst[idx] = src[idx];
                        continue;
                    }

                    let count = 0;
                    if (x > 0 && src[idx - 1] > threshold) {
                        count++;
                    }
                    if (x < nx - 1 && src[idx + 1] > threshold) {
                        count++;
                    }
                    if (y > 0 && src[idx - nx] > threshold) {
                        count++;
                    }
                    if (y < ny - 1 && src[idx + nx] > threshold) {
                        count++;
                    }
                    if (z > 0 && pixels[z - 1][idx] > threshold) {
                        count++;
                    }
                    if (z < nz - 1 && pixels[z + 1][idx] > threshold) {
                        count++;
                    }

                    dst[idx] = count >= minNeighbors ? src[idx] : 0;
                }
            }

            result.push(dst);
        }

        return result;
    }

    /**
     * Extracts DICOM contours from resampled SDF pixel data using
     * marching squares on each slice. Uses Grid.toWorldSpace for
     * correct coordinate mapping.
     */
    private static extractContours(
        pixels: Uint8Array[],
        sdfGrid: Grid,
        targetImage: Image3D
    ): DicomContour[] {
        const nx = sdfGrid.sizeX;
        const ny = sdfGrid.sizeY;
        const nz = sdfGrid.sizeZ;
        const resolution = sdfGrid.resX;
        const threshold = ContourTexture.SDF_THRESHOLD;
        const minAreaMm2 = resolution * resolution * 4;

        const dicomContours: DicomContour[] = [];

        for (let z = 0; z < nz; z++) {
            const contours2d = marchingSquares(
                pixels[z],
                nx,
                ny,
                1,
                1,
                threshold
            );

            if (contours2d.length === 0) {
                continue;
            }

            const sliceWorldPos = sdfGrid.toWorldSpace(
                vec3.fromValues(0, 0, z + 0.5)
            );
            const normalDir = targetImage.grid.getUnitZ();
            const targetSlice = this.findNearestImageSlice(
                vec3.dot(normalDir, sliceWorldPos),
                targetImage
            );

            for (const c2d of contours2d) {
                const pixelArea = this.computePolygonArea(c2d);
                const areaMm2 = pixelArea * resolution * resolution;
                if (areaMm2 < minAreaMm2) {
                    continue;
                }

                const simplified = simplify(c2d, 0.1, true);
                if (simplified.length < 3) {
                    continue;
                }

                const points3d = simplified.map((p) =>
                    sdfGrid.toWorldSpace(vec3.fromValues(p[0], p[1], z + 0.5))
                );

                dicomContours.push(
                    new DicomContour(
                        ContourGeometricType.CLOSED_PLANAR,
                        points3d,
                        targetSlice?.sopClassUid ?? '',
                        targetSlice?.sopInstanceUid ?? ''
                    )
                );
            }
        }

        return dicomContours;
    }

    // Finds the nearest image slice in the target image to a given normal position.
    private static findNearestImageSlice(
        normalPosition: number,
        targetImage: Image3D
    ): { sopClassUid: string; sopInstanceUid: string } | null {
        let bestSlice = null;
        let bestDist = Infinity;

        const normalDir = targetImage.grid.getUnitZ();
        for (const slice of targetImage.imageSlices) {
            const dist = Math.abs(
                vec3.dot(normalDir, slice.imagePosition) - normalPosition
            );
            if (dist < bestDist) {
                bestDist = dist;
                bestSlice = {
                    sopClassUid: targetImage.sopClassUid,
                    sopInstanceUid: slice.sopInstanceUid,
                };
            }
        }

        return bestSlice;
    }

    private static computePolygonArea(points: vec2[]): number {
        if (points.length < 3) {
            return 0;
        }
        let area = 0;
        for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
            area += points[j][0] * points[i][1];
            area -= points[i][0] * points[j][1];
        }
        return Math.abs(area) / 2;
    }
}
